import React, { useEffect, useState } from 'react'
import DynamicTable from '../../components/DynamicTable'
import CssBaseline from '@material-ui/core/CssBaseline'
import { API } from 'aws-amplify';
import OrderForm from './OrderForm';
import useUIState from '../../hooks/useUIstate';
import { useTranslation } from 'react-i18next';
import { TextField, Select, MenuItem, makeStyles } from '@material-ui/core';

const useStyles = makeStyles(() => ({
    filters: {
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
        '& .filter-group': {
            display: 'flex',
            flexDirection: 'column',
        },
        '& label': {
            fontSize: '0.8rem',
            marginBottom: '4px',
        },
        '& .MuiOutlinedInput-root': {
            minWidth: '200px',
        },
    },
}));

enum CollectOrderState {
    InProgress = "InProgress",
    ReadyForPickup = "ReadyForPickup",
    PickedUp = "PickedUp",
    Canceled = "Canceled",
}

function OrdersTable() {
    const classes = useStyles();
    const { setShowLoadingModal, setToastMessage, setShowToastMessage, setToastType } = useUIState();
    const [data, setData]: [any[], any] = useState([]);
    const [Stores, setStores]: [any[], any] = useState([]);
    const [showForm, setShowForm]: [boolean, any] = useState(false);
    const [formValues, setFormValues]: [any, any] = useState({});
    const [activeRow, setActiveRow]: [any, any] = useState(null);
    const [formMode, setFormMode]: [string, any] = useState("");
    const [searchText, setSearchText] = useState("");
    const [filterStore, setFilterStore] = useState("");
    const [filterState, setFilterState] = useState("InProgress");
    const { t } = useTranslation();

    const stateTranslations: Record<string, string> = {
        'InProgress': t('orderstable.state.in-progress'),
        'ReadyForPickup': t('orderstable.state.ready-for-pickup'),
        'PickedUp': t('orderstable.state.picked-up'),
        'Canceled': t('orderstable.state.canceled'),
    };

    useEffect(() => {
        localStorage.setItem("activeRoute", "/orders");
        getStores();
        // eslint-disable-next-line
    }, [])

    async function getStores() {
        API.get("AE", `/Stores`, { response: true })
            .then(res => {
                setStores(res.data);
                getOrders();
            })
            .catch(error => {
                console.log(error);
            });
    }

    async function getOrders() {
        setShowLoadingModal(true);
        API.get("AE", `/Orders`, { response: true })
            .then(res => {
                console.log(res.data);
                const ordersWithTranslatedState = res.data.map((order: any) => ({
                    ...order,
                    StateDisplay: stateTranslations[order.State] || order.State,
                    LockerCount: order.Lockers ? order.Lockers.length : 0
                }));
                setData(ordersWithTranslatedState);
            })
            .catch(error => {
                console.log(error);
                setToastType("error");
                setToastMessage(t('orderstable.error.getting-orders'));
                setShowToastMessage(true);
            })
            .finally(() => {
                setShowLoadingModal(false);
            });
    }

    const columns = React.useMemo(
        () => [
            {
                Header: 'Id',
                accessor: 'Id',
                hide: true,
            },
            {
                Header: t('orderstable.order-number'),
                accessor: 'OrderNumber',
            },
            {
                Header: t('orderstable.title'),
                accessor: 'Title',
            },
            {
                Header: t('orderstable.store'),
                accessor: 'Store',
            },
            {
                Header: t('orderstable.date'),
                accessor: 'OrderDate',
            },
            {
                Header: t('orderstable.status'),
                accessor: 'StateDisplay',
            },
            {
                Header: t('orderstable.locker-count'),
                accessor: 'LockerCount',
            },
        ],
        []
    );

    const filteredData = data.filter((order) => {
        const matchesSearch = searchText === "" || [
            order.OrderNumber, order.Title, order.Store, order.Email, order.Freetext
        ].some(field => field && field.toLowerCase().includes(searchText.toLowerCase()));

        const matchesStore = filterStore === "" || order.Store === filterStore;
        const matchesState = filterState === "" || order.State === filterState;

        return matchesSearch && matchesStore && matchesState;
    });

    function prepForm(row) {
        const rowValues = row !== null ? row.original : null;
        const addingRow = rowValues === null;

        const _formValues = {
            Id: addingRow ? 0 : rowValues.Id,
            StoreId: addingRow ? (Stores.length > 0 ? Stores[0].Id : "") : rowValues.StoreId,
            Title: addingRow ? "" : rowValues.Title,
            OrderNumber: addingRow ? "" : rowValues.OrderNumber,
            Email: addingRow ? "" : rowValues.Email,
            Freetext: addingRow ? "" : rowValues.Freetext,
            State: addingRow ? CollectOrderState.InProgress : rowValues.State,
            OrderDate: addingRow ? new Date() : new Date(rowValues.OrderDate),
            ReadyForPickupDate: addingRow ? null : (rowValues.ReadyForPickupDate ? new Date(rowValues.ReadyForPickupDate) : null),
            PickupDate: addingRow ? null : (rowValues.PickupDate ? new Date(rowValues.PickupDate) : null),
            HasLockers: addingRow ? false : (rowValues.Lockers && rowValues.Lockers.length > 0),
        };

        setActiveRow(rowValues);
        setFormMode(rowValues === null ? "Add" : "Edit");
        setFormValues(_formValues);
        setShowForm(true);
    }

    async function submit(values) {
        setShowLoadingModal(true);
        setShowForm(false);

        const orderData = {
            Id: values.Id,
            StoreId: values.StoreId,
            Title: values.Title,
            OrderNumber: values.OrderNumber,
            Email: values.Email,
            Freetext: values.Freetext,
            State: values.State,
            OrderDate: values.OrderDate,
            ReadyForPickupDate: values.ReadyForPickupDate,
            PickupDate: values.PickupDate,
        };

        try {
            if (activeRow === null) {
                // Create new order
                await API.post("AE", `/Orders`, { body: orderData });
                setToastType("success");
                setToastMessage(t('orderstable.order-created'));
                setShowToastMessage(true);
            } else {
                // Update existing order
                await API.put("AE", `/Orders`, { body: orderData });
                setToastType("success");
                setToastMessage(t('orderstable.order-updated'));
                setShowToastMessage(true);
            }
            getOrders();
        } catch (error) {
            console.log(error);
            setToastType("error");
            setToastMessage(activeRow === null ? t('orderstable.error.creating-order') : t('orderstable.error.updating-order'));
            setShowToastMessage(true);
            setShowLoadingModal(false);
        }
    }

    async function readyAndSend(values) {
        setShowLoadingModal(true);
        setShowForm(false);

        const orderData = {
            Id: values.Id,
            StoreId: values.StoreId,
            Title: values.Title,
            OrderNumber: values.OrderNumber,
            Email: values.Email,
            Freetext: values.Freetext,
            State: values.State,
            OrderDate: values.OrderDate,
            ReadyForPickupDate: values.ReadyForPickupDate,
            PickupDate: values.PickupDate,
        };

        try {
            await API.post("AE", `/Orders/SendPickupMailAndMarkReadyForPickup`, { body: orderData });
            setToastType("success");
            setToastMessage(t('orderstable.ready-and-send-success'));
            setShowToastMessage(true);
            getOrders();
        } catch (error) {
            console.log(error);
            setToastType("error");
            setToastMessage(t('orderstable.error.ready-and-send'));
            setShowToastMessage(true);
            setShowLoadingModal(false);
        }
    }

    const uniqueStores = Array.from(new Set(data.map((order) => order.Store))).filter(Boolean).sort();

    return (
        <div>
            <h1>{t('orderstable.orders')}</h1>
            <CssBaseline />
            <div className={classes.filters}>
                <div className="filter-group">
                    <label>{t('orderstable.filter.search')}</label>
                    <TextField
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        variant="outlined"
                        size="small"
                        placeholder={t('orderstable.filter.search-placeholder') as string}
                    />
                </div>
                <div className="filter-group">
                    <label>{t('orderstable.store')}</label>
                    <Select
                        value={filterStore}
                        onChange={(e) => setFilterStore(e.target.value as string)}
                        variant="outlined"
                        displayEmpty
                    >
                        <MenuItem value="">{t('orderstable.filter.all')}</MenuItem>
                        {uniqueStores.map((store) => (
                            <MenuItem key={store} value={store}>{store}</MenuItem>
                        ))}
                    </Select>
                </div>
                <div className="filter-group">
                    <label>{t('orderstable.status')}</label>
                    <Select
                        value={filterState}
                        onChange={(e) => setFilterState(e.target.value as string)}
                        variant="outlined"
                        displayEmpty
                    >
                        <MenuItem value="">{t('orderstable.filter.all')}</MenuItem>
                        <MenuItem value={CollectOrderState.InProgress}>{t('orderstable.state.in-progress')}</MenuItem>
                        <MenuItem value={CollectOrderState.ReadyForPickup}>{t('orderstable.state.ready-for-pickup')}</MenuItem>
                        <MenuItem value={CollectOrderState.PickedUp}>{t('orderstable.state.picked-up')}</MenuItem>
                        <MenuItem value={CollectOrderState.Canceled}>{t('orderstable.state.canceled')}</MenuItem>
                    </Select>
                </div>
            </div>
            <DynamicTable
                addRow={() => prepForm(null)}
                editRow={(row) => prepForm(row)}
                columns={columns}
                data={filteredData}
                loading={false}
            />
            {showForm ?
                <OrderForm
                    mode={formMode}
                    initialValues={formValues}
                    stores={Stores}
                    onClose={() => setShowForm(false)}
                    onSubmit={submit}
                    onReadyAndSend={readyAndSend}
                />
                : null}
        </div>
    )
}

export default OrdersTable;
